import InvitationRequestPage, {
  BatchInvitationRequestPage,
} from "@/views/Pages/Invitation/InvitationRequestPage";
import { BatchSigningPage } from "@/signing/BatchSigningPage";
import {
  mapToCompaniesTab,
  mapToSignaturesTab,
  parsePortfolioUrlSegment,
} from "@/portfolio/portfolioDomain";
import HelpCenter from "@/views/Pages/HelpCenter";
import { PortfolioShareholderPage } from "@/portfolio/contacts/shareholders/PortfolioShareholderPage.tsx";
import { AdminInfoPage, PortfolioContactsPage } from "@/portfolio/contacts/contactsComponents";
import { PortfolioCompaniesPage } from "@/portfolio/companies/companiesComponents";
import ResolveNoticeForShareholderPage from "@/meetings/generalMeeting/ResolveNoticeForShareholderPage";
import { FollowUpSignaturesPage } from "@/portfolio/followUp/FollowUpSignaturesPage";
import { observer } from "mobx-react";
import { useDispatch, useRootStore, useUserStore } from "@/stores/RootStoreContext";
import { isUserRoute } from "./routes";
import { FollowUpDecisionsPage } from "@/portfolio/followUp/FollowUpDecisionsPage";
import { EnterpriseAccountPage } from "@/enterpriseAccounts/EnterpriseAccountPage";
import { FollowUpCompanyInfoPage } from "@/portfolio/followUp/FollowUpCompanyInfoPage";
import { FollowUpShareholdersPage } from "@/portfolio/followUp/FollowUpShareholdersPage";
import { CustomRoleDetails } from "@/portfolio/contacts/customRoleComponents";
import { AggregatedContactDetailsPage } from "@/portfolio/contacts/AggregatedContactDetailsPage";
import { PortfolioSearchPage } from "@/portfolio/search/PortfolioSearchPage";
import { UserSettingsPage } from "@/userSettings/UserSettingsPage.tsx";
import { ReportsPage } from "@/portfolio/reports/ReportsPage";
import { ReportsResultPage } from "@/portfolio/reports/ReportsResultPage";
import { mapToUserSettingsTab } from "@/userSettings/userSettingsDomain";
import { QueryParams, singleValue, toQueryString } from "@/utils/queryParams";
import { defaultPortfolioUrl } from "@/utils/navigation";
import { useEffect } from "react";

const DefaultPortfolioRedirect = observer(({ queryParams }: { queryParams: QueryParams }) => {
  const userStore = useUserStore();
  const dispatch = useDispatch();
  const url =
    defaultPortfolioUrl(userStore.availablePortfolioFeatures) + toQueryString(queryParams);

  useEffect(() => {
    dispatch([{ type: "navigate", url, replace: true }]);
  }, [dispatch, url]);

  return null;
});

const UserRoutes = observer(() => {
  const rootStore = useRootStore();
  const route = rootStore.route && isUserRoute(rootStore.route) ? rootStore.route : undefined;
  switch (route?.name) {
    case "root/redirect":
    case "portfolio/redirect":
      return <DefaultPortfolioRedirect queryParams={route.queryParams} />;
    case "portfolio/companies":
      return (
        <PortfolioCompaniesPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          companiesTab={mapToCompaniesTab(route.params.tab)}
        />
      );
    case "portfolio/follow-up-signatures":
      return (
        <FollowUpSignaturesPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          activeHeader={"follow-up"}
          signaturesTab={mapToSignaturesTab(route.params.tab)}
        />
      );
    case "portfolio/follow-up-matters":
      return (
        <FollowUpDecisionsPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          tab={route.params.tab}
        />
      );
    case "portfolio/follow-up-company-info":
      return <FollowUpCompanyInfoPage {...parsePortfolioUrlSegment(route.params.portfolio)} />;
    case "portfolio/follow-up-shareholders":
      return (
        <FollowUpShareholdersPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          tab={route.params.tab}
        />
      );
    case "portfolio/contacts":
      return (
        <PortfolioContactsPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          tab={route.params.tab}
        />
      );
    case "portfolio/shareholder":
      return (
        <PortfolioShareholderPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          shareholderId={route.params.shareholderId}
          activeHeader={"contacts"}
          activeTab={route.params.activeTab}
        />
      );
    case "portfolio/contacts-admins":
      return (
        <AdminInfoPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          externalId={route.params.externalId}
          activeHeader={"contacts"}
        />
      );
    case "portfolio/aggregated-companyrole-details":
      return (
        <AggregatedContactDetailsPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          id={route.params.companyroleId}
          tab={"companyRole"}
        />
      );
    case "portfolio/aggregated-shareholder-details":
      return (
        <AggregatedContactDetailsPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          id={route.params.shareholderId}
          tab={"shareholder"}
        />
      );
    case "portfolio/contacts-custom-role":
      return (
        <PortfolioContactsPage
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          tab={"custom-role"}
          customRoleId={route.params.customRoleId}
        />
      );
    case "portfolio/custom-role-details":
      return (
        <CustomRoleDetails
          {...parsePortfolioUrlSegment(route.params.portfolio)}
          customRoleId={route.params.customRoleId}
          externalId={route.params.externalId}
        />
      );
    case "portfolio/search":
      return <PortfolioSearchPage />;
    case "portfolio/reports":
      return <ReportsPage />;
    case "portfolio/reports/result":
      return <ReportsResultPage />;
    case "help-center":
      return <HelpCenter portfolioType={"all-companies"} />;
    case "portfolio/help-center":
      return <HelpCenter {...parsePortfolioUrlSegment(route.params.portfolio)} isPortfolioPage />;
    case "invitation-request":
      return <InvitationRequestPage invitationRequestId={route.params.invitationRequestId} />;
    case "batch-invitation-request":
      return <BatchInvitationRequestPage batchId={route.params.batchId} />;
    case "user/settings":
      return <UserSettingsPage userSettingsTab={mapToUserSettingsTab(route.params.tab)} />;
    case "signing":
      return <BatchSigningPage signingMethod={route.params.signingMethod} />;
    case "notice-for-shareholder":
      return <ResolveNoticeForShareholderPage documentId={route.params.documentId} />;
    case "enterprise-account":
      return (
        <EnterpriseAccountPage
          id={route.params.id}
          tab={route.params.tab}
          accountSettingsTab={route.params.accountSettingsTab}
          templateFilter={route.params.templateFilter}
          field={singleValue(route.queryParams.field)}
          templateId={singleValue(route.queryParams.template)}
        />
      );
    default:
      return null;
  }
});

export default UserRoutes;
